// Laporan Kutipan (Prompt F) — senarai kutipan ikut julat tarikh + kaedah, jumlah, export CSV, cetak.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Printer, Loader2, Banknote } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { formatRM } from '../../../lib/format';
import { toCsv, downloadCsv, printLaporan, todayStr, monthStartStr } from '../../../lib/laporan';
import PageHeader from '../../../components/PageHeader';
import { CardSkeleton } from '../../../components/Skeletons';
import EmptyState from '../../../components/EmptyState';
import DataTable from '../../../components/DataTable';
import { Button } from '../../../components/ui/button';

const KAEDAH = { tunai: 'Tunai', transfer: 'Pindahan', qr_manual: 'QR Manual' };

export default function LaporanKutipan() {
  const { t } = useTranslation();
  const [dari, setDari] = useState(monthStartStr());
  const [hingga, setHingga] = useState(todayStr());
  const [kaedah, setKaedah] = useState('semua');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('payments')
      .select('id, jumlah_rm, kaedah, tanggal, nota, invoices!inner(tariff_id, student_id), students!inner(nama, class_id)')
      .eq('voided', false)
      .gte('tanggal', dari).lte('tanggal', hingga + 'T23:59:59')
      .order('tanggal', { ascending: false });
    if (kaedah !== 'semua') q = q.eq('kaedah', kaedah);
    const { data: pays } = await q;
    const tids = [...new Set((pays || []).map((p) => p.invoices?.tariff_id).filter(Boolean))];
    const cids = [...new Set((pays || []).map((p) => p.students?.class_id).filter(Boolean))];
    const [{ data: tariffs }, { data: classes }] = await Promise.all([
      tids.length ? supabase.from('tariffs').select('id, nama').in('id', tids) : { data: [] },
      cids.length ? supabase.from('classes').select('id, nama').in('id', cids) : { data: [] },
    ]);
    const tMap = Object.fromEntries((tariffs || []).map((x) => [x.id, x.nama]));
    const cMap = Object.fromEntries((classes || []).map((x) => [x.id, x.nama]));
    setRows((pays || []).map((p) => ({
      ...p,
      pelajar: p.students?.nama || '-', kelas: cMap[p.students?.class_id] || '-',
      tarif: tMap[p.invoices?.tariff_id] || 'Yuran',
    })));
    setLoading(false);
  }, [dari, hingga, kaedah]);

  useEffect(() => { load(); }, [load]);
  const total = useMemo(() => rows.reduce((a, r) => a + Number(r.jumlah_rm), 0), [rows]);
  const byKaedah = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.kaedah] = (m[r.kaedah] || 0) + Number(r.jumlah_rm); });
    return m;
  }, [rows]);

  const doCsv = () => downloadCsv(`laporan-kutipan-${dari}_${hingga}.csv`, toCsv(rows, [
    { label: 'Tarikh', get: (r) => new Date(r.tanggal).toLocaleString('ms-MY') },
    { label: 'Pelajar', get: (r) => r.pelajar },
    { label: 'Kelas', get: (r) => r.kelas },
    { label: 'Yuran', get: (r) => r.tarif },
    { label: 'Kaedah', get: (r) => KAEDAH[r.kaedah] || r.kaedah },
    { label: 'Jumlah (RM)', get: (r) => Number(r.jumlah_rm).toFixed(2) },
    { label: 'Nota', get: (r) => r.nota || '' },
  ]));

  const inputCls = 'h-10 px-3 rounded-xl border border-border bg-card text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-brand';

  return (
    <div>
      <div className="print:hidden">
        <PageHeader title={t('lapKutipan')} subtitle={t('lapKutipanSub')} breadcrumbs={[{ label: t('laporan') }, { label: t('lapKutipan') }]} />
      </div>
      <div className="hidden print:block mb-4">
        <h1 className="text-xl font-black">{t('lapKutipan')}</h1>
        <p className="text-sm text-muted-foreground">{dari} → {hingga}</p>
      </div>

      <div className="print:hidden mb-4 flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold text-muted-foreground">{t('dari')}<input type="date" value={dari} onChange={(e) => setDari(e.target.value)} className={`${inputCls} ml-2`} /></label>
        <label className="text-xs font-semibold text-muted-foreground">{t('hingga')}<input type="date" value={hingga} onChange={(e) => setHingga(e.target.value)} className={`${inputCls} ml-2`} /></label>
        <label className="text-xs font-semibold text-muted-foreground">{t('kaedah')}
          <select value={kaedah} onChange={(e) => setKaedah(e.target.value)} className={`${inputCls} ml-2`}>
            <option value="semua">{t('semua')}</option>
            {Object.entries(KAEDAH).map(([k]) => <option key={k} value={k}>{t(k)}</option>)}
          </select>
        </label>
        <Button onClick={load} className="h-10 bg-brand text-white hover:bg-brand-royal">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t('tapis')}</Button>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={doCsv} className="h-10"><Download className="h-4 w-4 mr-1.5" />CSV</Button>
          <Button variant="outline" onClick={() => printLaporan(t('lapKutipan'))} className="h-10"><Printer className="h-4 w-4 mr-1.5" />{t('cetak')}</Button>
        </div>
      </div>

      {loading ? <CardSkeleton count={3} /> : rows.length === 0 ? (
        <EmptyState icon={Banknote} title={t('tiadaData')} description={t('tiadaDataDesc')} />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('jumlahKutipan')}</p>
              <p className="text-xl sm:text-2xl font-black font-mono tabular text-emerald-600 dark:text-emerald-400">{formatRM(total)}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-4">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('bilTransaksi')}</p>
              <p className="text-xl sm:text-2xl font-black tabular text-foreground">{rows.length}</p>
            </div>
            {Object.entries(byKaedah).slice(0, 2).map(([k, v]) => (
              <div key={k} className="rounded-2xl border border-border bg-card p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{t(k) || k}</p>
                <p className="text-xl sm:text-2xl font-black font-mono tabular text-foreground">{formatRM(v)}</p>
              </div>
            ))}
          </div>
          <DataTable
            testid="kutipan"
            rows={rows}
            searchKeys={[(r) => r.pelajar, (r) => r.kelas, (r) => r.tarif]}
            statusOptions={[
              { key: 'tunai', label: t('tunai'), filter: (r) => r.kaedah === 'tunai' },
              { key: 'transfer', label: t('transfer'), filter: (r) => r.kaedah === 'transfer' },
              { key: 'qr_manual', label: t('qr_manual'), filter: (r) => r.kaedah === 'qr_manual' },
            ]}
            columns={[
              { key: 'tarikh', header: t('tarikh'), sortable: true, sortValue: (r) => r.tanggal, className: 'whitespace-nowrap',
                render: (r) => new Date(r.tanggal).toLocaleDateString('ms-MY', { dateStyle: 'medium' }) },
              { key: 'pelajar', header: t('pelajar'), sortable: true, sortValue: (r) => r.pelajar, render: (r) => <span className="block truncate font-medium text-foreground">{r.pelajar}</span> },
              { key: 'kelas', header: t('kelas'), sortable: true, sortValue: (r) => r.kelas, render: (r) => r.kelas },
              { key: 'yuran', header: t('yuran'), sortable: true, sortValue: (r) => r.tarif, render: (r) => r.tarif },
              { key: 'kaedah', header: t('kaedah'), render: (r) => t(r.kaedah) || r.kaedah },
              { key: 'jumlah', header: t('jumlah'), align: 'right', sortable: true, sortValue: (r) => Number(r.jumlah_rm),
                render: (r) => <span className="font-mono font-bold tabular">{formatRM(r.jumlah_rm)}</span> },
            ]}
            tfoot={(
              <tr className="bg-muted font-bold">
                <td colSpan={5} className="px-5 py-3 text-right">{t('jumlahKutipan')}</td>
                <td className="px-5 py-3 font-mono tabular text-right">{formatRM(total)}</td>
              </tr>
            )}
            empty={{ icon: Banknote, title: t('tiadaData'), description: t('tiadaDataDesc') }}
          />
        </>
      )}
    </div>
  );
}
